import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Calculator } from "lucide-react";
import Seo from "@/components/Seo";
import { Kicker } from "@/components/form";

export default function Rechner() {
  const [price, setPrice] = useState(400000);
  const [equity, setEquity] = useState(80000);
  const [rate, setRate] = useState(3.6);
  const [years, setYears] = useState(30);

  const { loan, monthly, totalInterest } = useMemo(() => {
    const loanAmt = Math.max(price - equity, 0);
    const r = rate / 100 / 12;
    const n = years * 12;
    const m = r > 0 ? (loanAmt * r) / (1 - Math.pow(1 + r, -n)) : loanAmt / n;
    return { loan: loanAmt, monthly: m, totalInterest: m * n - loanAmt };
  }, [price, equity, rate, years]);

  const fmt = (v) => new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 }).format(v);
  const Slider = ({ label, value, set, min, max, step, suffix }) => (
    <div>
      <div className="flex justify-between text-sm mb-2"><span className="text-slate-300">{label}</span><span className="text-gold-500 font-semibold">{fmt(value)} {suffix}</span></div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => set(Number(e.target.value))} className="w-full accent-gold-500" data-testid={`slider-${label}`} />
    </div>
  );

  return (
    <div data-testid="rechner-page" className="bg-navy-900 min-h-screen">
      <Seo title="Finanzierungsrechner" description="Berechnen Sie Ihre monatliche Rate für die Baufinanzierung – kostenloser Rechner von VM-FFM.DE." path="/finanzierung/rechner" keywords="Baufinanzierung Rechner, Kreditrechner, monatliche Rate berechnen" />
      <section className="pt-40 pb-24">
        <div className="max-w-4xl mx-auto px-6">
          <Kicker>Finanzierungsrechner</Kicker>
          <h1 className="mt-5 text-4xl sm:text-5xl font-extrabold tracking-tighter text-white leading-none flex items-center gap-4"><Calculator className="text-gold-500 hidden sm:block" size={42} strokeWidth={1.4} /> Ihre Rate berechnen</h1>
          <p className="mt-5 text-slate-300">Ein erster Anhaltspunkt – Ihr persönliches Angebot erstellen wir gern individuell.</p>
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-navy-800 border border-white/10 rounded-md p-8 space-y-7">
              <Slider label="Kaufpreis" value={price} set={setPrice} min={100000} max={2000000} step={10000} suffix="€" />
              <Slider label="Eigenkapital" value={equity} set={setEquity} min={0} max={800000} step={5000} suffix="€" />
              <Slider label="Sollzins" value={rate} set={setRate} min={1} max={7} step={0.1} suffix="%" />
              <Slider label="Laufzeit" value={years} set={setYears} min={5} max={35} step={1} suffix="Jahre" />
            </div>
            <div className="bg-gradient-to-br from-navy-800 to-navy-700 border border-gold-500/20 rounded-md p-8 flex flex-col justify-center">
              <p className="text-sm text-slate-400">Darlehenssumme</p>
              <p className="text-2xl font-bold text-white">{fmt(loan)} €</p>
              <div className="my-6 border-t border-white/10" />
              <p className="text-sm text-slate-400">Monatliche Rate</p>
              <p data-testid="monthly-rate" className="font-serif text-5xl text-gold-500">{fmt(monthly)} €</p>
              <p className="mt-6 text-sm text-slate-400">Gesamte Zinskosten: <span className="text-white font-semibold">{fmt(totalInterest)} €</span></p>
              <Link to="/anfrage/finanzierung-bau" className="mt-8 bg-gold-500 text-navy-900 font-bold rounded-sm py-3.5 text-center hover:bg-gold-300 transition-colors">Jetzt Finanzierung anfragen</Link>
            </div>
          </div>
          <p className="mt-6 text-xs text-slate-500">Hinweis: Unverbindliche Beispielrechnung (Annuitätendarlehen) ohne Berücksichtigung von Nebenkosten, Tilgungssatzänderungen und Gebühren.</p>
        </div>
      </section>
    </div>
  );
}
